import React, { useCallback, useState, useMemo } from 'react'
import { Text, CurrencyInputField, utils, constants } from '@adc/ui-core'
import { AuctionBlueprintScheduledOnlineReactiveArgs } from '@adc/blueprint'
import * as graph from '@adc/sdk'
import { internationalization } from '@adc/constants'
import { debounce } from 'lodash'
import _cx from 'classnames/bind'
import { Maybe } from '@adc/sdk/types'
import cls from '../styles.scss'
import { ConfirmingBid } from '../costs'

const cx = _cx.bind(cls)

const { ColorNames } = constants

type MaxBidContentProps = {
  bidAmount: number
  segment: AuctionBlueprintScheduledOnlineReactiveArgs['segment']
  buyersPremium: number
  totalPurchasePrice: number
  earnestMoneyDeposit: number
  onChange: (value?: number, isValid?: boolean) => void
  canAdjustOrRemove?: boolean
  isRemoveConfirmation?: boolean
}

const MaxBidContent = ({
  bidAmount,
  segment,
  buyersPremium,
  totalPurchasePrice,
  earnestMoneyDeposit,
  onChange,
  canAdjustOrRemove = false,
  isRemoveConfirmation = false,
}: MaxBidContentProps) => {
  const currentMaxBidAmount = useMemo(
    () =>
      segment?.__methods__?.hasValidMaxBid?.() &&
      segment?.current_user_max_bid?.max_bid_amount,
    [segment]
  )

  const [amount, setAmount] = useState<Maybe<string>>(() =>
    canAdjustOrRemove && currentMaxBidAmount
      ? utils.formatters.formatMoney(currentMaxBidAmount, {})
      : null
  )

  const minBidAmount = useMemo(
    () =>
      canAdjustOrRemove
        ? segment.__methods__.getNextBidAmount()
        : segment.__methods__.getNextMaxBidAmount(),
    [segment, canAdjustOrRemove]
  )

  const bidInputError = `Bid amount must be at least ${utils.formatters.formatMoney(
    minBidAmount,
    {}
  )}.`

  const [error, setError] = useState(false)
  const [touched, setTouched] = useState(false)

  const debouncedOnError = useCallback(
    debounce((hasError: boolean) => setError(hasError), 500),
    [onChange]
  )

  const allowDecimal = useMemo(
    () =>
      segment.configuration.precision_unit ===
      graph.enums.MoneyDenomination.Cent,
    [segment]
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const bidValue = e.target.value || ''
      const bidAmount = utils.currency.parse(bidValue)
      const hasError = Boolean(
        !bidAmount || (minBidAmount && bidAmount < minBidAmount)
      )
      setTouched(true)
      setAmount(bidValue)
      onChange(bidAmount, !hasError)
      debouncedOnError(hasError)
    },
    [debouncedOnError, minBidAmount, onChange]
  )

  return (
    <>
      {!isRemoveConfirmation && (
        <Text
          className={cx('amount-label', 'u-mb-2')}
          size='tiny'
          color={ColorNames.Gray}
        >
          {internationalization.enter_max_bid_label}
        </Text>
      )}
      {!!currentMaxBidAmount && (
        <div className={cx('bid-pill-container')}>
          <div className={cx('bid-pill')} data-elm-id='amount_to_bid'>
            <span>{`${internationalization.current_max_bid_label}:`}</span>
            <span>{utils.formatters.formatMoney(currentMaxBidAmount, {})}</span>
          </div>
        </div>
      )}
      {isRemoveConfirmation && (
        <Text size='small' className={cx('u-mb-2')}>
          {internationalization.remove_max_bid_message}
        </Text>
      )}
      {!isRemoveConfirmation && (
        <>
          <div className='ui form'>
            <CurrencyInputField
              name='bidAmount'
              onChange={handleChange}
              value={amount}
              className='u-mb-2'
              placeholder='Enter amount here'
              elmId='max_bid_amount_input'
              allowDecimal={allowDecimal}
              touched={touched}
              error={error ? bidInputError : undefined}
              autoFocus
            />
            {canAdjustOrRemove && (
              <Text
                size='tiny'
                color={ColorNames.GrayMedium}
                data-elm-id='min_bid_amount_hint'
              >
                {`Minimum: ${utils.formatters.formatMoney(minBidAmount, {})}`}
              </Text>
            )}
          </div>
          <div className={cx('u-mb-3')}>
            <ConfirmingBid
              bidAmount={bidAmount || 0}
              buyersPremium={buyersPremium}
              totalPurchasePrice={totalPurchasePrice}
              earnestMoneyDeposit={earnestMoneyDeposit}
              isHighestBid
            />
          </div>
        </>
      )}
    </>
  )
}

export default MaxBidContent
